/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { getScopeLevel } from '../utils/scope';
import { getLoginPath } from '../constants/loginRoutes';

/**
 * A wrapper for routes that requires authentication and optionally specific permissions.
 * Uses global permission context for consistent permission checking.
 */
const ProtectedRoute = ({ children, permissions = [], requireAll = false, platformOnly = false, excludeScopes = [] }) => {
  const { user, loading: authLoading } = useAuth();
  const { canAccessRoute, loading: permissionLoading } = usePermissions();
  const location = useLocation();

  // Show loading state while checking authentication and permissions
  if (authLoading || permissionLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4 bg-slate-950/5">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">Loading...</p>
      </div>
    );
  }

  // Check if user is authenticated
  if (!user) {
    // Redirect to login but save the current location to return after login
    return <Navigate to={getLoginPath()} state={{ from: location }} replace />;
  }

  // Restrict to platform-scoped users, regardless of permissions held
  if (platformOnly && user.scope !== 'platform') {
    return <Navigate to="/dashboard" replace />;
  }

  // Block access when the currently switched-into context matches an excluded scope
  if (excludeScopes.length > 0 && excludeScopes.includes(getScopeLevel(user))) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check permissions if any are required
  if (permissions.length > 0) {
    const permitted = canAccessRoute(location.pathname, permissions);
    if (!permitted) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
  platformOnly: PropTypes.bool,
  excludeScopes: PropTypes.arrayOf(PropTypes.string),
};
